"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "default", {
    enumerable: true,
    get: function() {
        return OAuthClient;
    }
});
const _crypto = require("crypto");
const _util = require("util");
const _nanoid = require("nanoid");
const _bcryptjs = /*#__PURE__*/ _interop_require_default(require("bcryptjs"));
const _nocodbsdk = require("nocodb-sdk");
const _globals = require("~/utils/globals");
const _Noco = /*#__PURE__*/ _interop_require_default(require("~/Noco"));
const _extractProps = require("~/helpers/extractProps");
const _modelUtils = require("~/utils/modelUtils");
const _NocoCache = /*#__PURE__*/ _interop_require_default(require("~/cache/NocoCache"));
const _serialize = require("~/utils/serialize");
const _index = require("~/models/index");
function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _interop_require_default(obj) {
    return obj && obj.__esModule ? obj : {
        default: obj
    };
}
let OAuthClient = class OAuthClient {
    static async insert(clientData, ncMeta = _Noco.default.ncMeta) {
        let insertData = (0, _extractProps.extractProps)(clientData, [
            'client_type',
            'client_name',
            'client_uri',
            'client_description',
            'logo_uri',
            'redirect_uris',
            'allowed_grant_types',
            'response_types',
            // 'allowed_scopes', TODO: Implement Scopes
            'registration_client_uri',
            'registration_access_token',
            'client_id_issued_at',
            'client_secret_expires_at',
            'fk_user_id'
        ]);
        insertData.client_id = (0, _nanoid.nanoid)(32);
        const clientType = insertData.client_type || _nocodbsdk.OAuthClientType.PUBLIC;
        insertData = _extends({}, insertData, {
            client_type: clientType,
            allowed_grant_types: [
                'authorization_code',
                'refresh_token'
            ],
            response_types: [
                'code'
            ],
            client_id_issued_at: Date.now()
        });
        let plaintextSecret;
        if (clientType === _nocodbsdk.OAuthClientType.CONFIDENTIAL) {
            plaintextSecret = (0, _crypto.randomBytes)(32).toString('base64url');
            const salt = await (0, _util.promisify)(_bcryptjs.default.genSalt)(10);
            insertData.client_secret = await (0, _util.promisify)(_bcryptjs.default.hash)(plaintextSecret, salt);
        }
        if (insertData.logo_uri) {
            insertData.logo_uri = this.serializeAttachmentJSON(insertData.logo_uri);
        }
        insertData = (0, _modelUtils.prepareForDb)(insertData, [
            'redirect_uris',
            'allowed_grant_types',
            'response_types'
        ]);
        const res = await ncMeta.metaInsert2(_globals.RootScopes.ROOT, _globals.RootScopes.ROOT, _globals.MetaTable.OAUTH_CLIENTS, insertData, true);
        return this.getByClientId(res.client_id, ncMeta).then(async (client)=>{
            await _NocoCache.default.appendToList('root', _globals.CacheScope.OAUTH_CLIENT, [], `${_globals.CacheScope.OAUTH_CLIENT}:${res.client_id}`);
            // Attach plaintext secret to response
            if (plaintextSecret) {
                client.client_secret = plaintextSecret;
            }
            return client;
        });
    }
    static async getByClientId(clientId, ncMeta = _Noco.default.ncMeta) {
        let data = await _NocoCache.default.get('root', `${_globals.CacheScope.OAUTH_CLIENT}:${clientId}`, _globals.CacheGetType.TYPE_OBJECT);
        if (!data) {
            data = await ncMeta.metaGet(_globals.RootScopes.ROOT, _globals.RootScopes.ROOT, _globals.MetaTable.OAUTH_CLIENTS, {
                client_id: clientId
            });
            if (data) {
                await _NocoCache.default.set('root', `${_globals.CacheScope.OAUTH_CLIENT}:${clientId}`, data);
            }
        }
        if (data && data.logo_uri) {
            const convertedAttachment = await this.convertAttachmentType({
                logo_uri: data.logo_uri
            }, ncMeta);
            data.logo_uri = convertedAttachment.logo_uri;
        }
        return data && await this.castType(data);
    }
    static async list(userId, ncMeta = _Noco.default.ncMeta) {
        const condition = userId ? {
            fk_user_id: userId
        } : {};
        const clients = await ncMeta.metaList2(_globals.RootScopes.ROOT, _globals.RootScopes.ROOT, _globals.MetaTable.OAUTH_CLIENTS, {
            condition
        });
        return await Promise.all(clients == null ? void 0 : clients.map(async (c)=>await this.castType(c)));
    }
    static async delete(clientId, ncMeta = _Noco.default.ncMeta) {
        if (!clientId) {
            return false;
        }
        await _NocoCache.default.deepDel('root', `${_globals.CacheScope.OAUTH_CLIENT}:${clientId}`, _globals.CacheDelDirection.CHILD_TO_PARENT);
        return await ncMeta.metaDelete(_globals.RootScopes.ROOT, _globals.RootScopes.ROOT, _globals.MetaTable.OAUTH_CLIENTS, {
            client_id: clientId
        });
    }
    static async regenerateSecret(clientId, ncMeta = _Noco.default.ncMeta) {
        if (!clientId) {
            return false;
        }
        // Generate plaintext secret for response
        const plaintextSecret = (0, _crypto.randomBytes)(32).toString('base64url');
        // Hash the secret for storage
        const salt = await (0, _util.promisify)(_bcryptjs.default.genSalt)(10);
        const hashedSecret = await (0, _util.promisify)(_bcryptjs.default.hash)(plaintextSecret, salt);
        await ncMeta.metaUpdate(_globals.RootScopes.ROOT, _globals.RootScopes.ROOT, _globals.MetaTable.OAUTH_CLIENTS, {
            client_secret: hashedSecret
        }, {
            client_id: clientId
        });
        await _NocoCache.default.update('root', `${_globals.CacheScope.OAUTH_CLIENT}:${clientId}`, {
            client_secret: hashedSecret
        });
        const client = await this.getByClientId(clientId, ncMeta);
        if (client) {
            client.client_secret = plaintextSecret;
        }
        return client;
    }
    static async update(clientId, body, ncMeta = _Noco.default.ncMeta) {
        if (!clientId) {
            return false;
        }
        let updateObj = (0, _extractProps.extractProps)(body, [
            'client_name',
            'client_uri',
            'client_description',
            'logo_uri',
            'redirect_uris',
            // 'allowed_scopes', TODO: Implement Scopes
            'registration_client_uri',
            'client_secret_expires_at'
        ]);
        updateObj = (0, _modelUtils.prepareForDb)(updateObj, [
            'redirect_uris',
            'allowed_grant_types',
            'response_types'
        ]);
        if (updateObj.logo_uri) {
            updateObj.logo_uri = this.serializeAttachmentJSON(updateObj.logo_uri);
        }
        await ncMeta.metaUpdate(_globals.RootScopes.ROOT, _globals.RootScopes.ROOT, _globals.MetaTable.OAUTH_CLIENTS, updateObj, {
            client_id: clientId
        });
        await _NocoCache.default.update('root', `${_globals.CacheScope.OAUTH_CLIENT}:${clientId}`, updateObj);
        return this.getByClientId(clientId, ncMeta);
    }
    static async castType(client) {
        if (!client) return null;
        client = (0, _modelUtils.prepareForResponse)(client, [
            'redirect_uris',
            'allowed_grant_types',
            'response_types'
        ]);
        if (client && client.logo_uri) {
            const convertedAttachment = await this.convertAttachmentType({
                logo_uri: client.logo_uri
            }, client);
            client.logo_uri = convertedAttachment.logo_uri;
        }
        return new OAuthClient(client);
    }
    static serializeAttachmentJSON(attachment) {
        if (attachment) {
            return (0, _serialize.serializeJSON)((0, _extractProps.extractProps)((0, _serialize.deserializeJSON)(attachment), [
                'url',
                'path',
                'title',
                'mimetype',
                'size',
                'icon'
            ]));
        }
        return attachment;
    }
    static async convertAttachmentType(attachmentObjs, ncMeta = _Noco.default.ncMeta) {
        try {
            if (attachmentObjs) {
                const promises = [];
                for(const key in attachmentObjs){
                    if (attachmentObjs[key] && typeof attachmentObjs[key] === 'string') {
                        attachmentObjs[key] = (0, _serialize.deserializeJSON)(attachmentObjs[key]);
                    }
                    promises.push(_index.PresignedUrl.signAttachment({
                        attachment: attachmentObjs[key]
                    }, ncMeta));
                }
                await Promise.all(promises);
            }
        } catch (unused) {}
        return attachmentObjs;
    }
    constructor(client){
        Object.assign(this, client);
    }
};
